import { Stack, Alert, ButtonProps, Button } from '@mui/material';
import { Formik, FormikConfig, Form as FormikForm } from 'formik';
import { FC, useEffect, useId, useMemo, useRef } from 'react';
import { Field, ComponentProps } from './types';

export interface Props<T = Record<string, string>> {
  onSubmit: FormikConfig<T>['onSubmit'];
  fields: Field[];
  loading?: boolean;
  error?: string;
  buttonProps?: ButtonProps;
  disabled?: boolean;
  componentMap: Record<string, FC<ComponentProps>>;
}

const Form: FC<Props> = ({ componentMap, buttonProps, onSubmit, disabled, loading, fields, error }) => {
  const id = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const initialValues = useMemo(() => {
    const values = fields.reduce((acc, field) => {
      if (field.type && field.name) {
        acc[field.name] = field.value ?? '';
      }
      return acc;
    }, {} as Record<string, string>);
    return values;
  }, [fields]);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.resetForm({ values: {} });
    }
  }, [formRef]);

  return (
    <Formik ref={formRef} initialValues={initialValues} onSubmit={onSubmit}>
      {({ getFieldProps }) => (
        <>
          <FormikForm id={id}>
            <Stack spacing={3} sx={{ my: 3 }}>
              {fields.map((field, i) => {
                const Component = componentMap[field.type] ?? componentMap['default'];
                return (
                  <Component
                    disabled={disabled}
                    key={String(i) + String(field.name)}
                    label={field.label}
                    {...field.options}
                    {...getFieldProps(field.name)}
                  />
                );
              })}
            </Stack>

            {error && (
              <Alert sx={{ my: 2 }} color="error">
                {error}
              </Alert>
            )}

            {!disabled && (
              <Button
                disabled={disabled}
                loading={loading}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                {...buttonProps}
              >
                {buttonProps?.children ?? 'Submit'}
              </Button>
            )}
          </FormikForm>
        </>
      )}
    </Formik>
  );
};

export default Form;
